import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { RevealItem, revealContainer } from '../common/Reveal';
import { format } from 'date-fns';
import { DownloadIcon, RefreshCwIcon } from 'lucide-react';
import { PageHeader } from '../layout/PageHeader';
import { Button } from '../common/Button';
import { TeamSelect } from '../common/TeamSelect';
import { SegmentedControl } from '../common/SegmentedControl';
import { SourceFilter } from '../common/SourceFilter';
import { KpiCards } from '../reports/KpiCards';
import { CallVolumeChart } from '../reports/CallVolumeChart';
import { CallInsights } from '../reports/CallInsights';
import { ManagerTable } from '../reports/ManagerTable';
import { BlandOverview } from '../bland/BlandOverview';
import { sourceLabel } from '../../utils/sources';
import { downloadCsv } from '../../utils/csv';
import { formatDuration, formatPercent } from '../../utils/format';
import { blandChartTitle, chartTitles, previousLabels, rangeOptions, scopeLabels } from '../../utils/range';
import { teamLabel } from '../../utils/teams';
import type { CallsInsightHubState } from '../CallsInsightHub/useCallsInsightHub';
export function ReportsView({
  hub
}: {
  hub: CallsInsightHubState;
}) {
  const [lastUpdated, setLastUpdated] = useState('4:58 PM');
  const [refreshing, setRefreshing] = useState(false);
  const scope = scopeLabels[hub.range];
  const sourceName = sourceLabel(hub.source);
  const isBland = hub.source === 'bland';
  const refresh = () => {
    setRefreshing(true);
    window.setTimeout(() => {
      setLastUpdated(format(new Date(), 'h:mm a'));
      setRefreshing(false);
    }, 450);
  };
  const exportCsv = () => {
    const file = `calls-report-${hub.source}-${hub.range}.csv`;
    if (isBland) {
      downloadCsv(file, ['Rank', 'Loan officer', 'Extension', 'Team', 'Transfers received', 'Opportunities', 'Transfer to opportunity'], hub.bland.rows.map(r => [r.rank, r.agent.name, r.agent.extension, teamLabel(r.agent.team), r.transfers, r.opportunities, formatPercent(r.conversion)]));
      return;
    }
    downloadCsv(file, ['Rank', 'User', 'Extension', 'Team', 'Source', 'Calls made', 'Answered', 'Missed', 'Answer rate', 'Talk time', 'Avg call'], hub.rankedTeamRows.map(r => [r.rank, r.agent.name, r.agent.extension, teamLabel(r.agent.team), sourceName, r.stats.outbound, r.stats.inbound, r.stats.missed, formatPercent(r.stats.answerRate), formatDuration(r.stats.talkSeconds), formatDuration(r.stats.avgSeconds)]));
  };
  return <>
      <PageHeader title="Reports" subtitle={`${hub.team === 'All' ? 'Team' : teamLabel(hub.team)} performance on ${sourceName}, ${scope}`} actions={<>
            <span className="text-xs text-subtle" aria-live="polite" data-id="element-799" data-mp="vu689yl1ixnn">
              Last updated {lastUpdated}
            </span>
            <Button iconOnly onClick={refresh} aria-label="Refresh data" disabled={refreshing} data-id="element-800" data-mp="354ymwfot8nk3">
              <RefreshCwIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden data-id="element-801" data-mp="305m4c74z22nb" />
            </Button>
            <Button variant="primary" onClick={exportCsv} data-id="element-802" data-mp="2v7ardnjdrc0v">
              <DownloadIcon className="h-4 w-4" aria-hidden data-id="element-803" data-mp="2v8vlxd5p2uyq" />
              Export
            </Button>
          </>} data-id="element-798" data-mp="1oeemr0am7pl0" />

      <RevealItem className="mb-6 flex flex-wrap items-center gap-3" data-id="element-804" data-mp="2lg6dys9crz85">
        <SourceFilter value={hub.source} onChange={hub.setSource} data-id="element-805" data-mp="2lev7gkoiwddc" />
        <TeamSelect value={hub.team} onChange={hub.setTeam} data-id="element-806" data-mp="2hfmevj57yljd" />
        <SegmentedControl label="Date range" options={rangeOptions} value={hub.range} onChange={hub.setRange} data-id="element-807" data-mp="2cgjkcsmv7xpn" />
      </RevealItem>

      {isBland ? <BlandOverview report={hub.bland} scopeLabel={scope} chartTitle={blandChartTitle(hub.range)} data-id="element-808" data-mp="27j1an010xegl" /> : <>
          <KpiCards totals={hub.totals} trend={hub.kpiTrend} previousLabel={previousLabels[hub.range]} data-id="element-809" data-mp="6epfpxit0l5p" />

          <motion.div variants={revealContainer} className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,2.1fr)_minmax(0,1fr)]" data-id="element-810" data-mp="bdiqhi6phy26">
            <RevealItem fill data-id="element-811" data-mp="3st8qlk8kli7f">
              <CallVolumeChart data={hub.volume} title={chartTitles[hub.range]} data-id="element-812" data-mp="3su0avywpvtie" />
            </RevealItem>
            <RevealItem fill data-id="element-813" data-mp="1lw7gzo4muov">
              <CallInsights hub={hub} data-id="element-814" data-mp="3jtvfkn4sslkl" />
            </RevealItem>
          </motion.div>

          <RevealItem className="mt-6" data-id="element-815" data-mp="3osotuhrwsglt">
            <ManagerTable summaries={hub.teamSummaries} activeTeam={hub.team} onTeamChange={hub.setTeam} data-id="element-816" data-mp="3otq4wm8xd33j" />
          </RevealItem>
        </>}
    </>;
}